import { useTranslations } from '@aikoa/i18n/react'
import { useCallback } from 'react'
import { View } from 'react-native'
import { Body } from '~/components/typography/Content'
import { Button } from '~/components/ui/button'
import { type InputVariant } from '~/components/ui/input'
import { OTPInput } from '~/components/ui/input-otp'
import { useOtpCooldownSeconds, type useOtpStateManager } from '~/lib/auth/useOtpStateManager'
import { cn } from '~/lib/utils'

type OtpVerificationProps = {
  className?: string
  disabled?: boolean
  inputTestId?: string
  inputVariant?: InputVariant
  onReset?: () => void
  onResend: () => void
  resetLabel?: string
  otpStateManager: ReturnType<typeof useOtpStateManager>
  pendingChange: string | null
  otp: string
  setOtp: (otp: string) => void
}

export function OtpVerification({
  className,
  disabled = false,
  inputTestId,
  inputVariant = 'box',
  onReset,
  onResend,
  resetLabel,
  otpStateManager,
  pendingChange,
  otp,
  setOtp,
}: OtpVerificationProps) {
  const { t } = useTranslations('common')
  const cooldownSeconds = useOtpCooldownSeconds(
    pendingChange,
    otpStateManager.getResendCooldownRemainingMs
  )

  const isCooldownActive = cooldownSeconds > 0

  const handleResend = useCallback(async () => {
    if (isCooldownActive) {
      return
    }

    onResend()
  }, [isCooldownActive, onResend])

  const resetOtp = useCallback(
    (callback?: () => void) => {
      setOtp('')
      callback?.()
    },
    [setOtp]
  )

  return (
    <View className={cn('w-full items-center gap-4', className)}>
      <OTPInput
        maxLength={6}
        value={otp}
        onChange={setOtp}
        onComplete={setOtp}
        testID={inputTestId}
        textContentType="oneTimeCode"
        variant={inputVariant}
        className="w-48 self-center"
        inputClassName="text-center"
        editable={!disabled}
      />

      <View className="flex-row justify-start gap-3">
        <View className="flex-1 flex-col items-center gap-2">
          <Button variant="link" size="sm" disabled={isCooldownActive} onPress={handleResend}>
            {t(($) => $.otp.resendCode)}
          </Button>

          {isCooldownActive && (
            <Body className="text-center text-sm text-neutral-500">
              {t(($) => $.otp.cooldownMessage, { seconds: cooldownSeconds })}
            </Body>
          )}
        </View>

        {onReset && (
          <View className="flex-1">
            <Button variant="link" size="sm" onPress={() => resetOtp(onReset)}>
              {resetLabel ?? t(($) => $.otp.resetChange)}
            </Button>
          </View>
        )}
      </View>
    </View>
  )
}
